/**
 * Session timeout configuration
 * Centralized configuration for session timeout behavior
 */

export interface SessionTimeoutConfig {
    // Session timeout duration in milliseconds
    sessionTimeoutMs: number

    // Warning threshold in milliseconds (when to show warning before timeout)
    warningThresholdMs: number

    // Check interval in milliseconds (how often to check for timeout)
    checkIntervalMs: number

    // Activity timeout in milliseconds (how long to wait after activity before extending session)
    activityTimeoutMs: number

    // Session extension duration in seconds (how long to extend session when user is active)
    sessionExtensionSeconds: number
}

// Default configuration
export const DEFAULT_SESSION_CONFIG: SessionTimeoutConfig = {
    // 8 hour session timeout (extended from 1h so the app does not bounce to the
    // login screen during demos / idle stretches; aligned with the ~8h refresh-token
    // window, beyond which the backend refresh expires anyway).
    sessionTimeoutMs: 8 * 60 * 60 * 1000,

    // Show warning 30 seconds before timeout
    warningThresholdMs: 30 * 1000,

    // Check every 10 seconds for responsive warnings
    checkIntervalMs: 10 * 1000,

    // Wait 5 seconds after activity before extending session
    activityTimeoutMs: 5 * 1000,

    // Extend session by 15 minutes when user is active
    sessionExtensionSeconds: 15 * 60,
}

// Environment-based configuration
export const getSessionConfig = (): SessionTimeoutConfig => {
    // You can override these with environment variables if needed
    const config = { ...DEFAULT_SESSION_CONFIG }

    // Override with environment variables if they exist
    if (process.env.REACT_APP_SESSION_TIMEOUT_MS) {
        config.sessionTimeoutMs = parseInt(process.env.REACT_APP_SESSION_TIMEOUT_MS, 10)
    }

    if (process.env.REACT_APP_SESSION_WARNING_THRESHOLD_MS) {
        config.warningThresholdMs = parseInt(process.env.REACT_APP_SESSION_WARNING_THRESHOLD_MS, 10)
    }

    if (process.env.REACT_APP_SESSION_CHECK_INTERVAL_MS) {
        config.checkIntervalMs = parseInt(process.env.REACT_APP_SESSION_CHECK_INTERVAL_MS, 10)
    }

    return config
}

// Helper functions
export const formatTimeRemaining = (milliseconds: number): string => {
    const seconds = Math.ceil(milliseconds / 1000)
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
}

export const isSessionExpired = (lastActivity: number, config: SessionTimeoutConfig): boolean => {
    const now = Date.now()
    return (now - lastActivity) > config.sessionTimeoutMs
}

export const shouldShowWarning = (lastActivity: number, config: SessionTimeoutConfig): boolean => {
    const now = Date.now()
    const timeUntilTimeout = config.sessionTimeoutMs - (now - lastActivity)
    return timeUntilTimeout <= config.warningThresholdMs && timeUntilTimeout > 0
}

export const getTimeUntilTimeout = (lastActivity: number, config: SessionTimeoutConfig): number => {
    const now = Date.now()
    return Math.max(0, config.sessionTimeoutMs - (now - lastActivity))
}
